/**
 * L'outillage commun des generateurs.
 *
 * Un generateur ne fabrique pas une `Question` complete: il rend un
 * `Brouillon`. L'assembleur (index.ts) y ajoute l'identifiant, le niveau et le
 * poids. C'est ce qui garantit que la difficulte vient de la TABLE de
 * progression et non de chaque generateur pris a part.
 */
import type { Alea } from '../alea.ts'
import type { SaisieQuestion } from './types.ts'

export interface Brouillon {
  enonce: string
  consigne: string
  saisie: SaisieQuestion
  reponses: readonly string[]
  choix?: readonly string[]
  unite?: string
  /**
   * 0 a 9: ce qui departage deux questions du MEME niveau et du meme rang
   * (la taille des nombres, la rarete du verbe). Borne a 9 pour que le poids
   * d'un niveau ne puisse jamais atteindre celui du niveau suivant.
   */
  taille: number
}

/** Un genre d'exercice, a un niveau donne. */
export interface Activite {
  /** Identifiant court, ecrit dans le journal et dans le resultat. */
  id: string
  /** 0, 1 ou 2: l'ordre a l'interieur du niveau. */
  rang: 0 | 1 | 2
  faire: (a: Alea) => Brouillon
}

/** Ramene une grandeur a un terme de 0 a 9, par ordre de grandeur. */
export function tailleDe(n: number): number {
  const v = Math.abs(n)
  return Math.max(0, Math.min(9, Math.round(Math.log10(Math.max(1, v)) * 3)))
}

/**
 * Compose la liste de boutons d'une question a choix.
 *
 * Les doublons sont retires — « pris » au masculin pluriel est « pris », et
 * proposer deux fois le meme bouton donne a l'enfant l'impression que le jeu
 * s'est trompe. L'ordre final est melange par `alea`, donc reproductible.
 */
export function composerChoix(a: Alea, bon: string, distracteurs: readonly string[], n = 4): readonly string[] {
  const vus = new Set<string>([bon])
  const liste: string[] = [bon]
  for (const d of distracteurs) {
    if (liste.length >= n) break
    if (vus.has(d)) continue
    vus.add(d)
    liste.push(d)
  }
  return a.melanger(liste)
}
